import { useState } from "react";
import { historicoSincronizacao, projetoAtivo, conflitoExemplo } from "@/mocks/coletor";

interface SyncPageProps {
  onClose: () => void;
}

export default function SyncPage({ onClose }: SyncPageProps) {
  const [sincronizando, setSincronizando] = useState(false);
  const [mostrarConflito, setMostrarConflito] = useState(false);
  const [progresso, setProgresso] = useState(0);

  const iniciarSync = () => {
    setSincronizando(true);
    setProgresso(0);
    const interval = setInterval(() => {
      setProgresso((p) => {
        if (p >= 100) {
          clearInterval(interval);
          setSincronizando(false);
          return 100;
        }
        return p + Math.random() * 20;
      });
    }, 400);
    setTimeout(() => {
      clearInterval(interval);
      setSincronizando(false);
      setMostrarConflito(true);
    }, 4000);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
      " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const statusSyncConfig: Record<string, { label: string; icon: string; color: string; bg: string }> = {
    sucesso: { label: "Concluído", icon: "ri-check-line", color: "text-[#1B6E4F]", bg: "bg-[#CAF0D3]" },
    erro_parcial: { label: "Erro parcial", icon: "ri-error-warning-line", color: "text-[#F57C00]", bg: "bg-[#FFF3E0]" },
    conflito: { label: "Conflito", icon: "ri-git-merge-line", color: "text-[#FBC02D]", bg: "bg-[#FFFDE7]" },
    erro: { label: "Falha", icon: "ri-close-circle-line", color: "text-[#D32F2F]", bg: "bg-[#FFEBEE]" },
  };

  return (
    <div className="px-4 pt-4 pb-20 max-w-lg mx-auto">
      <div className="flex items-center gap-2 mb-5">
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center">
          <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
        </button>
        <h2 className="text-lg font-heading font-bold text-[#3F4A52]">Sincronização</h2>
      </div>

      {/* Status atual */}
      <div className="bg-white border border-[#E8EAED] rounded-xl p-4 mb-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${sincronizando ? "bg-[#64B5F6]/20" : "bg-[#CAF0D3]"}`}>
            <i className={`${sincronizando ? "ri-refresh-line animate-spin text-[#0052CC]" : "ri-wifi-line text-[#1B6E4F]"} text-lg`} />
          </div>
          <div>
            <p className="text-sm font-heading font-bold text-[#3F4A52]">
              {sincronizando ? "Sincronizando..." : "Online"}
            </p>
            <p className="text-xs text-[#3F4A52]/60">
              Última: {formatDate(projetoAtivo.ultimaSincronizacao)}
            </p>
          </div>
        </div>

        {sincronizando && (
          <div className="mb-3">
            <div className="h-1.5 bg-[#E8EAED] rounded-full overflow-hidden">
              <div className="h-full bg-[#0052CC] rounded-full transition-all duration-300" style={{ width: `${Math.min(progresso, 100)}%` }} />
            </div>
            <p className="text-[10px] text-[#3F4A52]/50 mt-1">{Math.round(progresso)}% concluído</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "Árvores pendentes", value: projetoAtivo.pendentesSincronizacao, color: "text-[#1B6E4F]" },
            { label: "Vistorias pendentes", value: 8, color: "text-[#0052CC]" },
            { label: "Fotos pendentes", value: projetoAtivo.fotosPendentes, color: "text-[#3F4A52]" },
            { label: "Erros", value: 2, color: "text-[#D32F2F]" },
          ].map((item, i) => (
            <div key={i} className="bg-[#E8EAED]/40 rounded-xl p-3 text-center">
              <p className={`text-lg font-heading font-bold ${item.color}`}>{item.value}</p>
              <p className="text-[10px] text-[#3F4A52]/60">{item.label}</p>
            </div>
          ))}
        </div>

        <button
          onClick={iniciarSync}
          disabled={sincronizando}
          className={`w-full mt-4 py-4 rounded-xl font-heading font-bold text-sm active:scale-[0.98] transition-transform ${
            sincronizando ? "bg-[#E8EAED] text-[#3F4A52]/40" : "bg-[#1B6E4F] text-white"
          }`}
        >
          <i className={`${sincronizando ? "ri-loader-4-line animate-spin" : "ri-cloud-line"} mr-1.5`} />
          {sincronizando ? "Sincronizando..." : "Sincronizar agora"}
        </button>
      </div>

      {/* Histórico */}
      <div>
        <h3 className="text-sm font-heading font-bold text-[#3F4A52] mb-3">Histórico de sincronização</h3>
        <div className="space-y-2">
          {historicoSincronizacao.map((h) => {
            const cfg = statusSyncConfig[h.status];
            return (
              <div key={h.id} className="flex items-start gap-3 p-3 rounded-xl border border-[#E8EAED] bg-white">
                <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${cfg.icon} ${cfg.color} text-sm`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[#3F4A52]">{formatDate(h.data)}</span>
                    <span className={`text-[10px] font-medium ${cfg.color} px-1.5 py-0.5 rounded-full ${cfg.bg}`}>{cfg.label}</span>
                  </div>
                  <p className="text-xs text-[#3F4A52]/50 mt-0.5">{h.registros} registros · {h.fotos} fotos · {h.duracao}</p>
                  {h.erro && <p className="text-[10px] text-[#D32F2F] mt-0.5">{h.erro}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de conflito */}
      {mostrarConflito && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-2xl p-5 max-w-lg mx-auto max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#FFFDE7] flex items-center justify-center">
                <i className="ri-git-merge-line text-[#FBC02D]" />
              </div>
              <h3 className="text-base font-heading font-bold text-[#3F4A52]">Conflito de Sincronização</h3>
            </div>

            <p className="text-sm text-[#3F4A52] mb-4">
              O registro <span className="font-mono text-[#0052CC]">{conflitoExemplo.local.id}</span> possui versões diferentes no dispositivo local e no servidor.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="border border-[#1B6E4F] rounded-xl p-3 bg-[#CAF0D3]/20">
                <p className="text-xs font-heading font-bold text-[#1B6E4F] mb-2">Local (dispositivo)</p>
                {Object.entries(conflitoExemplo.local).map(([k, v]) => (
                  <p key={k} className="text-xs text-[#3F4A52]"><span className="font-medium">{k}:</span> {v}</p>
                ))}
              </div>
              <div className="border border-[#0052CC] rounded-xl p-3 bg-[#64B5F6]/10">
                <p className="text-xs font-heading font-bold text-[#0052CC] mb-2">Servidor</p>
                {Object.entries(conflitoExemplo.servidor).map(([k, v]) => (
                  <p key={k} className="text-xs text-[#3F4A52]"><span className="font-medium">{k}:</span> {v}</p>
                ))}
              </div>
            </div>

            <p className="text-xs text-[#F57C00] mb-4 flex items-center gap-1">
              <i className="ri-error-warning-line" /> Diferenças: {conflitoExemplo.diferencas.join("; ")}
            </p>

            <div className="space-y-2">
              <button onClick={() => setMostrarConflito(false)} className="w-full py-3 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm">
                Manter versão local
              </button>
              <button onClick={() => setMostrarConflito(false)} className="w-full py-3 rounded-xl bg-[#0052CC] text-white font-heading font-bold text-sm">
                Manter versão do servidor
              </button>
              <button onClick={() => setMostrarConflito(false)} className="w-full py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52]">
                Mesclar manualmente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}